import React, { useState, useEffect, useRef } from 'react';
import type { Event, EventMealSlot } from '../../types';
import { uploadBookingHeroImage } from '../../lib/bookingHeroImageUpload';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => void;
  event?: Event | null;
}

type SlotRow = EventMealSlot & { slotLimit: string };

function newSlotId(): string {
  return `slot-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const PRESET_SLOTS: { label: string; startTime: string; endTime: string }[] = [
  { label: 'Brunch', startTime: '10:00', endTime: '14:00' },
  { label: 'Lunch', startTime: '12:00', endTime: '15:00' },
  { label: 'Dinner', startTime: '17:30', endTime: '21:30' },
];

const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  event,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerEventDetails, setCustomerEventDetails] = useState('');
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [extraDates, setExtraDates] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('21:00');
  const [maxCapacity, setMaxCapacity] = useState<string>('');
  const [kidsAgeUnder, setKidsAgeUnder] = useState<string>('');
  const [maxGuestsPerSlot, setMaxGuestsPerSlot] = useState<string>('');
  const [mealSlotRows, setMealSlotRows] = useState<SlotRow[]>([]);
  const [parkingInstructions, setParkingInstructions] = useState('');
  const [showTableInCustomerComms, setShowTableInCustomerComms] = useState(true);
  const [bookingFormMode, setBookingFormMode] = useState<'standard' | 'buffet_multi_slot'>('standard');
  const [capacitySource, setCapacitySource] = useState<'slot' | 'tables' | 'both' | ''>('');
  const [bookingHeroImageUrl, setBookingHeroImageUrl] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);
  const heroFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      setName(event.name);
      setDescription(event.description || '');
      setCustomerEventDetails(event.customerEventDetails || '');
      setEventDate(event.eventDate);
      const ed = event.eventDates?.filter(Boolean) ?? [];
      const primary = event.eventDate;
      setExtraDates(ed.filter((d) => d !== primary));
      setStartTime(event.startTime);
      setEndTime(event.endTime);
      setMaxCapacity(event.maxCapacity != null ? String(event.maxCapacity) : '');
      setKidsAgeUnder(event.kidsAgeUnder != null ? String(event.kidsAgeUnder) : '');
      setMaxGuestsPerSlot(event.maxGuestsPerSlot != null ? String(event.maxGuestsPerSlot) : '');
      const limits = event.slotGuestLimits ?? {};
      setMealSlotRows(
        (event.mealSlots ?? []).map((s) => ({
          ...s,
          slotLimit: limits[s.id] != null ? String(limits[s.id]) : '',
        }))
      );
      setParkingInstructions(event.parkingInstructions || '');
      setShowTableInCustomerComms(event.showTableInCustomerComms !== false);
      setBookingFormMode(event.bookingFormMode ?? (event.mealSlots?.length ? 'buffet_multi_slot' : 'standard'));
      setCapacitySource(event.capacitySource ?? '');
      setBookingHeroImageUrl(event.bookingHeroImageUrl || '');
    } else {
      setName('');
      setDescription('');
      setCustomerEventDetails('');
      setEventDate(new Date().toISOString().split('T')[0]);
      setExtraDates([]);
      setStartTime('18:00');
      setEndTime('21:00');
      setMaxCapacity('');
      setKidsAgeUnder('');
      setMaxGuestsPerSlot('');
      setMealSlotRows([]);
      setParkingInstructions('');
      setShowTableInCustomerComms(true);
      setBookingFormMode('standard');
      setCapacitySource('');
      setBookingHeroImageUrl('');
    }
  }, [event, isOpen]);

  const handleBookingHeroFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setHeroUploading(true);
    try {
      const url = await uploadBookingHeroImage(file, event?.id ?? null);
      setBookingHeroImageUrl(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not upload image');
    } finally {
      setHeroUploading(false);
    }
  };

  const allDatesSorted = [...new Set([eventDate, ...extraDates].filter(Boolean))].sort();

  const handleSaveClick = () => {
    if (!name.trim()) {
      alert('Please enter event name');
      return;
    }
    const cleanedRows = mealSlotRows.filter((r) => r.label.trim() && r.startTime);
    const slots: EventMealSlot[] = cleanedRows.map((r) => ({
      id: r.id?.trim() ? r.id : newSlotId(),
      label: r.label.trim(),
      startTime: r.startTime,
      endTime: r.endTime?.trim() || undefined,
    }));
    const slotGuestLimits: Record<string, number> = {};
    cleanedRows.forEach((row, idx) => {
      if (row.slotLimit === '') return;
      const n = parseInt(String(row.slotLimit), 10);
      if (Number.isFinite(n) && n >= 0) slotGuestLimits[slots[idx].id] = n;
    });
    const primaryDate = allDatesSorted[0] ?? eventDate;
    const eventDatesOut = allDatesSorted.length > 1 ? allDatesSorted : undefined;

    onSave({
      name: name.trim(),
      description: description.trim(),
      customerEventDetails: customerEventDetails.trim(),
      bookingHeroImageUrl: bookingHeroImageUrl.trim(),
      eventDate: primaryDate,
      eventDates: eventDatesOut,
      startTime,
      endTime,
      maxCapacity:
        maxCapacity === '' ? undefined : Math.max(0, parseInt(String(maxCapacity), 10) || 0),
      kidsAgeUnder:
        kidsAgeUnder === ''
          ? undefined
          : Math.max(0, Math.min(120, parseInt(String(kidsAgeUnder), 10) || 0)) || undefined,
      mealSlots: slots.length > 0 ? slots : undefined,
      maxGuestsPerSlot:
        maxGuestsPerSlot === ''
          ? undefined
          : Math.max(0, parseInt(String(maxGuestsPerSlot), 10) || 0),
      slotGuestLimits: Object.keys(slotGuestLimits).length > 0 ? slotGuestLimits : undefined,
      parkingInstructions: parkingInstructions.trim(),
      showTableInCustomerComms,
      bookingFormMode,
      capacitySource: capacitySource === '' ? undefined : capacitySource,
    });
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const tag = target.tagName.toLowerCase();
    if (tag === 'textarea') return;
    // Prevent mobile keyboard "Done"/Enter from implicitly submitting the modal.
    e.preventDefault();
  };

  const addDateRow = () => setExtraDates((d) => [...d, '']);
  const updateExtraDate = (i: number, v: string) =>
    setExtraDates((d) => d.map((x, j) => (j === i ? v : x)));
  const removeExtraDate = (i: number) => setExtraDates((d) => d.filter((_, j) => j !== i));

  const addSlotRow = () =>
    setMealSlotRows((rows) => [
      ...rows,
      { id: newSlotId(), label: '', startTime: '12:00', endTime: '', slotLimit: '' },
    ]);
  const updateSlotRow = (i: number, patch: Partial<SlotRow>) =>
    setMealSlotRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeSlotRow = (i: number) => setMealSlotRows((rows) => rows.filter((_, j) => j !== i));

  const applyPresets = () => {
    setMealSlotRows(
      PRESET_SLOTS.map((p) => ({
        id: newSlotId(),
        label: p.label,
        startTime: p.startTime,
        endTime: p.endTime,
        slotLimit: '',
      }))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {event ? 'Edit Event' : 'New Event'}
        </h2>

        <form onSubmit={(e) => e.preventDefault()} onKeyDown={handleFormKeyDown} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Internal / short summary..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Public event details (booking page)
            </label>
            <textarea
              value={customerEventDetails}
              onChange={(e) => setCustomerEventDetails(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Longer text shown on the public booking page (plain text, line breaks OK)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Booking page hero image (optional)
            </label>
            <input
              ref={heroFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleBookingHeroFile}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => heroFileInputRef.current?.click()}
                disabled={heroUploading}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {heroUploading ? 'Uploading…' : 'Upload image'}
              </button>
              {bookingHeroImageUrl ? (
                <button
                  type="button"
                  onClick={() => setBookingHeroImageUrl('')}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove image
                </button>
              ) : null}
            </div>
            <p className="text-xs text-gray-500 mt-2 mb-1">Or paste a direct image URL:</p>
            <input
              type="url"
              value={bookingHeroImageUrl}
              onChange={(e) => setBookingHeroImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="https://… (optional if you uploaded above)"
            />
            {bookingHeroImageUrl ? (
              <div className="mt-2 rounded-md border border-gray-200 overflow-hidden bg-gray-50 max-h-32">
                <img
                  src={bookingHeroImageUrl}
                  alt="Hero preview"
                  className="w-full h-28 object-cover object-center"
                  onError={(ev) => {
                    (ev.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : null}
            <p className="text-xs text-gray-500 mt-1">
              Wide banners (e.g. 1200×400) work best. Uploads are stored in your Firebase project (max 5MB).
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Event dates</span>
              <button
                type="button"
                onClick={addDateRow}
                className="text-sm text-pink-600 hover:text-pink-700"
              >
                + Add date
              </button>
            </div>
            <p className="text-xs text-gray-500">
              First date is the primary list date. Add more for multi-day events.
            </p>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Primary date *</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            {extraDates.map((d, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  type="date"
                  value={d}
                  onChange={(e) => updateExtraDate(i, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <button
                  type="button"
                  onClick={() => removeExtraDate(i)}
                  className="text-sm text-red-600 px-2"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max capacity (tables / venue cap)
              </label>
              <input
                type="number"
                min="0"
                placeholder="Optional"
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kids age under (standard booking form)
              </label>
              <input
                type="number"
                min="0"
                max="120"
                placeholder="e.g. 12"
                value={kidsAgeUnder}
                onChange={(e) => setKidsAgeUnder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-800">Meal slots (buffet / hosted)</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={applyPresets}
                  className="text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Brunch / Lunch / Dinner
                </button>
                <button
                  type="button"
                  onClick={addSlotRow}
                  className="text-sm text-pink-600 hover:text-pink-700"
                >
                  + Slot
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Each slot gets a stable ID for reporting. Per-slot guest limit overrides the default below.
            </p>
            {mealSlotRows.length === 0 ? (
              <p className="text-sm text-gray-400">No slots — standard single-time booking.</p>
            ) : (
              <div className="space-y-2">
                {mealSlotRows.map((row, i) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-12 gap-2 items-end border-b border-gray-100 pb-2"
                  >
                    <div className="col-span-12 sm:col-span-4">
                      <label className="text-xs text-gray-600">Label</label>
                      <input
                        type="text"
                        value={row.label}
                        onChange={(e) => updateSlotRow(i, { label: e.target.value })}
                        className="w-full px-2 py-1.5 border rounded-md text-sm"
                        placeholder="e.g. Sunday Brunch"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <label className="text-xs text-gray-600">Start</label>
                      <input
                        type="time"
                        value={row.startTime}
                        onChange={(e) => updateSlotRow(i, { startTime: e.target.value })}
                        className="w-full px-2 py-1.5 border rounded-md text-sm"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <label className="text-xs text-gray-600">End</label>
                      <input
                        type="time"
                        value={row.endTime || ''}
                        onChange={(e) => updateSlotRow(i, { endTime: e.target.value })}
                        className="w-full px-2 py-1.5 border rounded-md text-sm"
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <label className="text-xs text-gray-600">Slot limit</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Default"
                        value={row.slotLimit}
                        onChange={(e) => updateSlotRow(i, { slotLimit: e.target.value })}
                        className="w-full px-2 py-1.5 border rounded-md text-sm"
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeSlotRow(i)}
                        className="text-sm text-red-600 py-2"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default max guests per slot
              </label>
              <input
                type="number"
                min="0"
                placeholder="No default slot cap"
                value={maxGuestsPerSlot}
                onChange={(e) => setMaxGuestsPerSlot(e.target.value)}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parking instructions</label>
            <textarea
              value={parkingInstructions}
              onChange={(e) => setParkingInstructions(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Shown on public booking and in confirmation email when set"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Public booking form</label>
              <select
                value={bookingFormMode}
                onChange={(e) =>
                  setBookingFormMode(e.target.value as 'standard' | 'buffet_multi_slot')
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="standard">Standard (party / optional kids split)</option>
                <option value="buffet_multi_slot">Buffet / multi-slot (date + meal + allergies)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity source</label>
              <select
                value={capacitySource}
                onChange={(e) => setCapacitySource(e.target.value as typeof capacitySource)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="">Auto (slot limit + tables when limits set)</option>
                <option value="slot">Slot guest limit only</option>
                <option value="tables">Tables / venue only</option>
                <option value="both">Both (must pass slot and tables)</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showTableInCustomerComms}
              onChange={(e) => setShowTableInCustomerComms(e.target.checked)}
              className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm text-gray-700">
              Show table assignment in customer emails and messaging
            </span>
          </label>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveClick}
              className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors"
            >
              {event ? 'Update' : 'Create'} Event
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;
